// Mobile Chat, after Slack's mobile redesign: a tinted header with search and a filter, then a rounded
// sheet. Home has quick tiles (Threads, Mentions, Reactions), Unread DMs, Starred, Channels and Direct
// messages; DMs has the people you talk to most on top and every conversation with its last message;
// Activity is one feed of mentions, thread replies, DMs and reactions.
import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import { format, isToday, isYesterday } from 'date-fns';
import { AtSign, Check, Hash, ListFilter, Lock, MessageSquareReply, Search, Smile, X } from '@/components/ui/icons';
import type { Channel, Message } from '@shared/types';
import { useDb, useMe, useStore } from '@/lib/store';
import { useWorkspaceId } from '@/lib/nav';
import { chatSectionsOf, openDm, sectionByChannel } from '@/lib/actions';
import { Avatar } from '@/components/ui/Avatar';
import { Popover } from '@/components/ui/Popover';
import { Menu, MenuItem, MenuLabel, MenuSeparator } from '@/components/ui/Menu';
import { WorkspaceLogo } from '@/shell/WorkspaceLogo';
import { channelTitle, useUnreadByChannel } from '@/shell/ChannelSections';
import { htmlToText, useStarredIds } from '@/pages/chat/utils';
import { markActivityRead, useActivity, type ActivityItem, type ActivityKind } from '@/pages/chat/activity';
import { openSheet } from './sheets';

type View = 'home' | 'dms' | 'activity';
type Filter = 'all' | 'unreads';
type ActivityFilter = 'all' | ActivityKind;

const FILTER_KEY = 'atrium.mChatFilter';
const readFilter = (): Filter => {
  try {
    return localStorage.getItem(FILTER_KEY) === 'unreads' ? 'unreads' : 'all';
  } catch {
    return 'all';
  }
};

function when(ts: number) {
  if (isToday(ts)) return format(ts, 'h:mm a');
  if (isYesterday(ts)) return 'Yesterday';
  return format(ts, 'MMM d');
}

function Badge({ n }: { n: number }) {
  return n ? <span className="mc-badge">{n > 99 ? '99+' : n}</span> : null;
}

/** A conversation row on Home: # name (or a person), bold while unread. */
function ConvoRow({ ch, unread }: { ch: Channel; unread: number }) {
  const ws = useWorkspaceId();
  const me = useMe();
  const navigate = useNavigate();
  const title = useDb((d) => channelTitle(d, ch, me));
  const other = ch.memberIds.find((u) => u !== me) ?? me;
  return (
    <button type="button" className={clsx('mc-row', { unread: unread > 0 })} onClick={() => navigate(`/${ws}/chat/${ch.id}`)}>
      <span className="mc-row-icon">{ch.kind === 'dm' ? <Avatar userId={other} size={24} showPresence className="mc-sq" /> : ch.private ? <Lock size={18} /> : <Hash size={19} />}</span>
      <span className="mc-row-name truncate">{title}</span>
      <Badge n={unread} />
    </button>
  );
}

function Section({ title, action, children }: { title: string; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="mc-section">
      <div className="mc-section-head">
        <span>{title}</span>
        {action}
      </div>
      {children}
    </section>
  );
}

function HomeView({ q, filter, onView }: { q: string; filter: Filter; onView: (v: View, a?: ActivityFilter) => void }) {
  const ws = useWorkspaceId();
  const me = useMe();
  const navigate = useNavigate();
  const channels = useDb((d) => d.channels);
  const unread = JSON.parse(useUnreadByChannel()) as Record<string, number>;
  const stars = useStarredIds();
  const activity = useActivity();
  const db = useDb((d) => d);
  const text = q.trim().toLowerCase();
  const mine = useMemo(
    () =>
      Object.values(channels)
        .filter((c) => c.workspaceId === ws && c.memberIds.includes(me))
        .filter((c) => !text || channelTitle(db, c, me).toLowerCase().includes(text))
        .filter((c) => filter === 'all' || unread[c.id])
        .sort((a, b) => channelTitle(db, a, me).localeCompare(channelTitle(db, b, me))),
    [channels, ws, me, text, filter, unread, db],
  );
  const count = (k: ActivityKind) => activity.filter((i) => i.kind === k && i.unread).length;
  const sections = chatSectionsOf(db, me, ws);
  const filed = sectionByChannel(db, me);
  const unreadDms = mine.filter((c) => c.kind === 'dm' && unread[c.id]);
  const starred = mine.filter((c) => stars.includes(c.id));
  const chans = mine.filter((c) => c.kind === 'channel' && !stars.includes(c.id) && !filed[c.id]);
  const dms = mine.filter((c) => c.kind === 'dm' && !unread[c.id] && !stars.includes(c.id) && !filed[c.id]);
  const tile = (key: ActivityKind, label: string, icon: React.ReactNode) => {
    const n = count(key);
    return (
      <button type="button" className={clsx('mc-tile', { on: n > 0 })} onClick={() => onView('activity', key)}>
        <span className="mc-tile-icon">{icon}</span>
        <span className="mc-tile-title">{label}</span>
        <span className="mc-tile-sub">{n ? `${n} new` : 'Nothing new'}</span>
      </button>
    );
  };
  return (
    <>
      {!text && (
        <div className="mc-tiles">
          {tile('thread', 'Threads', <MessageSquareReply size={17} />)}
          {tile('mention', 'Mentions', <AtSign size={17} />)}
          {tile('reaction', 'Reactions', <Smile size={17} />)}
        </div>
      )}
      {unreadDms.length > 0 && (
        <Section
          title="Unread DMs"
          action={
            <button type="button" className="mc-link" onClick={() => onView('dms')}>
              See all {mine.filter((c) => c.kind === 'dm').length}
            </button>
          }
        >
          {unreadDms.map((c) => (
            <ConvoRow key={c.id} ch={c} unread={unread[c.id] ?? 0} />
          ))}
        </Section>
      )}
      {starred.length > 0 && (
        <Section title="Starred">
          {starred.map((c) => (
            <ConvoRow key={c.id} ch={c} unread={unread[c.id] ?? 0} />
          ))}
        </Section>
      )}
      {/* my sidebar sections (made on desktop or from a conversation's settings) */}
      {sections.map((sec) => {
        const items = mine.filter((c) => filed[c.id] === sec.id && !stars.includes(c.id));
        return items.length > 0 ? (
          <Section key={sec.id} title={sec.name}>
            {items.map((c) => (
              <ConvoRow key={c.id} ch={c} unread={unread[c.id] ?? 0} />
            ))}
          </Section>
        ) : null;
      })}
      {chans.length > 0 && (
        <Section title="Channels">
          {chans.map((c) => (
            <ConvoRow key={c.id} ch={c} unread={unread[c.id] ?? 0} />
          ))}
        </Section>
      )}
      {dms.length > 0 && (
        <Section title="Direct messages">
          {dms.map((c) => (
            <ConvoRow key={c.id} ch={c} unread={0} />
          ))}
        </Section>
      )}
      {mine.length === 0 && (
        <div className="m-empty">
          <strong>{filter === 'unreads' ? 'You’re all caught up' : text ? 'No conversations match' : 'No conversations yet'}</strong>
          <span>{filter === 'unreads' ? 'Unread conversations show up here.' : 'Start one from the + button.'}</span>
        </div>
      )}
      <button type="button" className="mc-browse" onClick={() => navigate(`/${ws}/chat?browse=1`)}>
        <Hash size={18} /> Browse all channels
      </button>
    </>
  );
}

function lastMessages(messages: Record<string, Message>) {
  const out: Record<string, Message> = {};
  for (const m of Object.values(messages)) if (!m.parentId && (!out[m.channelId] || out[m.channelId].createdAt < m.createdAt)) out[m.channelId] = m;
  return out;
}

function DmsView({ q, filter }: { q: string; filter: Filter }) {
  const ws = useWorkspaceId();
  const me = useMe();
  const navigate = useNavigate();
  const db = useDb((d) => d);
  const online = useStore((s) => s.presence);
  const unread = JSON.parse(useUnreadByChannel()) as Record<string, number>;
  const last = useMemo(() => lastMessages(db.messages), [db.messages]);
  const text = q.trim().toLowerCase();
  const dms = Object.values(db.channels)
    .filter((c) => c.workspaceId === ws && c.kind === 'dm' && c.memberIds.includes(me))
    .filter((c) => !text || channelTitle(db, c, me).toLowerCase().includes(text))
    .filter((c) => filter === 'all' || unread[c.id])
    .sort((a, b) => (last[b.id]?.createdAt ?? b.createdAt) - (last[a.id]?.createdAt ?? a.createdAt));
  // people on top: whoever you talked to most recently, then everyone else in the workspace
  const people = useMemo(() => {
    const order: string[] = [];
    for (const c of dms) for (const u of c.memberIds) if (u !== me && !order.includes(u)) order.push(u);
    for (const u of db.workspaces[ws]?.memberIds ?? []) if (u !== me && !order.includes(u)) order.push(u);
    return order.slice(0, 12);
  }, [dms, db.workspaces, ws, me]);
  const isOnline = (u: string) => online.some((p) => p.userId === u);
  return (
    <>
      {!text && (
        <div className="mc-people">
          {people.map((u) => (
            <button key={u} type="button" className="mc-person" onClick={() => navigate(`/${ws}/chat/${openDm(ws, u).id}`)}>
              <span className="mc-person-photo">
                <Avatar userId={u} size={56} className="mc-sq" />
                <span className={clsx('mc-person-dot', { on: isOnline(u) })} />
              </span>
              <span className="mc-person-name truncate">{db.users[u]?.name.split(' ')[0]}</span>
            </button>
          ))}
        </div>
      )}
      {dms.map((c) => {
        const m = last[c.id];
        const other = c.memberIds.find((u) => u !== me) ?? me;
        const n = unread[c.id] ?? 0;
        return (
          <button key={c.id} type="button" className={clsx('mc-dm', { unread: n > 0 })} onClick={() => navigate(`/${ws}/chat/${c.id}`)}>
            <Avatar userId={other} size={40} showPresence className="mc-sq" />
            <span className="mc-dm-text">
              <span className="mc-dm-top">
                <span className="mc-dm-name truncate">{channelTitle(db, c, me)}</span>
                {m && <span className="mc-dm-time">{when(m.createdAt)}</span>}
              </span>
              <span className="mc-dm-bottom">
                <span className="mc-dm-preview">{m ? `${m.authorId === me ? 'You: ' : ''}${htmlToText(m.body) || 'Sent a file'}` : 'No messages yet'}</span>
                <Badge n={n} />
              </span>
            </span>
          </button>
        );
      })}
      {dms.length === 0 && (
        <div className="m-empty">
          <strong>{filter === 'unreads' ? 'No unread messages' : 'No direct messages yet'}</strong>
          <span>Tap someone above to start a conversation.</span>
        </div>
      )}
    </>
  );
}

const KIND_LABEL: Record<ActivityKind, string> = { mention: 'Mention', thread: 'Thread reply', dm: 'Direct message', reaction: 'Reaction' };
const ACTIVITY_FILTERS: { key: ActivityFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'mention', label: 'Mentions' },
  { key: 'thread', label: 'Threads' },
  { key: 'reaction', label: 'Reactions' },
  { key: 'dm', label: 'DMs' },
];

function ActivityRow({ item }: { item: ActivityItem }) {
  const ws = useWorkspaceId();
  const me = useMe();
  const navigate = useNavigate();
  const db = useDb((d) => d);
  const actor = db.users[item.actorId];
  const where = item.channel.kind === 'dm' ? channelTitle(db, item.channel, me) : `#${item.channel.name}`;
  const open = () => {
    markActivityRead([item]);
    const m = item.message;
    navigate(`/${ws}/chat/${item.channel.id}?${m.parentId ? `thread=${m.parentId}&` : ''}m=${m.id}`);
  };
  return (
    <button type="button" className={clsx('mc-act', { unread: item.unread })} onClick={open}>
      <span className="mc-act-head">
        {item.kind === 'mention' ? <AtSign size={13} /> : item.kind === 'thread' ? <MessageSquareReply size={13} /> : item.kind === 'reaction' ? <Smile size={13} /> : null}
        <span className="truncate">
          {KIND_LABEL[item.kind]} in {where}
        </span>
        <span className="mc-act-time">{when(item.at)}</span>
        {item.unread && <span className="mc-act-dot" />}
      </span>
      <span className="mc-act-body">
        <Avatar userId={item.actorId} size={36} className="mc-sq" />
        <span className="mc-act-text">
          <span className="mc-act-name">
            {item.kind === 'reaction' && item.reactorIds && item.reactorIds.length > 1 ? `${actor?.name ?? 'Someone'} and ${item.reactorIds.length - 1} more reacted` : item.kind === 'reaction' ? `${actor?.name ?? 'Someone'} reacted` : (actor?.name ?? 'Someone')}
          </span>
          <span className="mc-act-msg">{htmlToText(item.message.body) || 'Sent a file'}</span>
          {item.kind === 'reaction' && item.message.reactions && (
            <span className="mc-act-reacts">
              {Object.entries(item.message.reactions).map(([emoji, users]) => (
                <span key={emoji} className="mc-react">
                  {emoji} {users.length}
                </span>
              ))}
            </span>
          )}
        </span>
      </span>
    </button>
  );
}

function ActivityView({ q, filter, unreadsOnly, onFilter }: { q: string; filter: ActivityFilter; unreadsOnly: boolean; onFilter: (f: ActivityFilter) => void }) {
  const text = q.trim().toLowerCase();
  const names = useDb((d) => d.users);
  const items = useActivity().filter(
    (i) =>
      (filter === 'all' || i.kind === filter) &&
      (!unreadsOnly || i.unread) &&
      (!text || htmlToText(i.message.body).toLowerCase().includes(text) || (i.channel.name ?? '').toLowerCase().includes(text) || (names[i.actorId]?.name ?? '').toLowerCase().includes(text)),
  );
  return (
    <>
      <div className="mc-chips">
        {ACTIVITY_FILTERS.map((f) => (
          <button key={f.key} type="button" className={clsx('mc-chip', { on: filter === f.key })} onClick={() => onFilter(f.key)}>
            {f.label}
          </button>
        ))}
      </div>
      {items.map((i) => (
        <ActivityRow key={i.key} item={i} />
      ))}
      {items.length === 0 && (
        <div className="m-empty">
          <strong>Nothing here yet</strong>
          <span>Mentions, replies in your threads, DMs and reactions to your messages show up here.</span>
        </div>
      )}
    </>
  );
}

export function MobileChat() {
  const ws = useWorkspaceId();
  const me = useMe();
  const [params, setParams] = useSearchParams();
  const view = (['dms', 'activity'].includes(params.get('view') ?? '') ? params.get('view') : 'home') as View;
  const [q, setQ] = useState('');
  const [filter, setFilterState] = useState<Filter>(readFilter);
  const [activityFilter, setActivityFilter] = useState<ActivityFilter>('all');
  const name = useDb((d) => d.workspaces[ws]?.name ?? 'Chat');
  const activity = useActivity();
  const unread = JSON.parse(useUnreadByChannel()) as Record<string, number>;
  const dmUnread = useDb((d) => Object.values(d.channels).some((c) => c.kind === 'dm' && c.workspaceId === ws && c.memberIds.includes(me) && unread[c.id]));
  const setFilter = (f: Filter) => {
    setFilterState(f);
    try {
      localStorage.setItem(FILTER_KEY, f);
    } catch {
      /* private mode */
    }
  };
  const go = (v: View, a?: ActivityFilter) => {
    const next = new URLSearchParams(params);
    if (v === 'home') next.delete('view');
    else next.set('view', v);
    setParams(next, { replace: true });
    if (a) setActivityFilter(a);
  };
  return (
    <div className="m-page mc">
      <header className="mc-head">
        <div className="mc-head-top">
          {view === 'home' ? (
            <button type="button" className="mc-ws" onClick={() => openSheet('workspaces')}>
              <WorkspaceLogo workspaceId={ws} size={30} />
              <span className="truncate">{name}</span>
            </button>
          ) : (
            <h1 className="mc-title">{view === 'dms' ? 'Direct Messages' : 'Activity'}</h1>
          )}
          <span className="spacer" />
          <button type="button" className="m-me" onClick={() => openSheet('profile')} aria-label="Your profile">
            <Avatar userId={me} size={32} showPresence />
          </button>
        </div>
        <div className="mc-search-row">
          <label className="mc-search">
            <Search size={17} />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={view === 'activity' ? 'Search activity' : 'Search conversations'} />
            {q && (
              <button type="button" className="m-icon-btn sm" onClick={() => setQ('')} aria-label="Clear search">
                <X size={15} />
              </button>
            )}
          </label>
          <Popover
            placement="bottom-end"
            content={(close) => (
              <Menu style={{ width: 230 }}>
                <MenuLabel>Filter</MenuLabel>
                <MenuItem label={view === 'activity' ? 'All activity' : 'All conversations'} right={filter === 'all' ? <Check size={14} /> : undefined} onClick={() => (setFilter('all'), close())} />
                <MenuItem label="Only unreads" right={filter === 'unreads' ? <Check size={14} /> : undefined} onClick={() => (setFilter('unreads'), close())} />
                {view === 'activity' && (
                  <>
                    <MenuSeparator />
                    <MenuItem label="Mark all as read" onClick={() => (markActivityRead(activity.filter((i) => i.unread)), close())} />
                  </>
                )}
              </Menu>
            )}
          >
            <button type="button" className={clsx('mc-filter', { on: filter === 'unreads' })} aria-label="Filter">
              <ListFilter size={18} />
            </button>
          </Popover>
        </div>
        <div className="mc-views" role="tablist">
          {(['home', 'dms', 'activity'] as View[]).map((v) => (
            <button key={v} type="button" role="tab" aria-selected={view === v} className={clsx('mc-view', { on: view === v })} onClick={() => go(v)}>
              {v === 'home' ? 'Home' : v === 'dms' ? 'DMs' : 'Activity'}
              {((v === 'dms' && dmUnread) || (v === 'activity' && activity.some((i) => i.unread))) && <span className="mc-view-dot" />}
            </button>
          ))}
        </div>
      </header>
      <div className="mc-sheet m-scroll">
        {view === 'home' ? (
          <HomeView q={q} filter={filter} onView={go} />
        ) : view === 'dms' ? (
          <DmsView q={q} filter={filter} />
        ) : (
          <ActivityView q={q} filter={activityFilter} unreadsOnly={filter === 'unreads'} onFilter={setActivityFilter} />
        )}
      </div>
    </div>
  );
}
